import { Person } from "../../../../../common/svg/Person";
import { ChangeEvent, JSX, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { useCreateCertificateMutation } from "../../../../../common/api/certificate";
import {
    setExpiresAtValue,
    setFormValues,
    setIsPostCreateCertificateSuccess,
    setValidAtValue,
} from "../../../CreateCertificateModalSlice";
import { addMonths, addYears, format } from "date-fns";
import { EmployeeInterface } from "../../../../../common/interfaces/EmployeeInterface";
import { handleMultipleCertificates } from "../../../utils/handleMultipleCertificates";
import { DateInputFieldComponent } from "../../../components/FormComponents/DateInputFieldComponent";
import { FileInputFieldComponent } from "../../../components/FormComponents/FileInputFieldComponent";
import { SelectFieldComponent } from "../../../../../common/components/SelectFieldComponent";
import { SubmitButtonComponent } from "../../../components/FormComponents/SubmitButtonComponent";
import { useGetEducatorsQuery } from "../../../../../common/api/educator";
import { getEducatorOptions } from "../../../../../common/utils/selectOptions/getEducatorOptions";
import { getEmployeeEducatorValue } from "../utils/getEmployeeEducatorValue";
import { getUserFullName } from "../../../../../common/utils/getUserFullName";
import { QualificationValidityTypeEnum } from "../../../../../common/enums/QualificationValidityTypeEnum";

export const CreateFormContentStepTwo = (): JSX.Element => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<HTMLDivElement[]>([]);
    const dispatch = useAppDispatch();
    const {
        selectedEmployees,
        validAtValues,
        expiresAtValues,
        selectedQualification,
        formValues,
        isLoading,
        error,
    } = useAppSelector((state) => state.createCertificateModal);
    const [file, setFile] = useState<{ [key: number]: File }>({});

    const [
        postCreateCertificate,
        { isSuccess: isPostCreateCertificateSuccess },
    ] = useCreateCertificateMutation();

    const { data: educatorData } = useGetEducatorsQuery({
        items_per_page: 9999,
        filter: {
            qualification_id: selectedQualification?.id?.toString(),
        },
    });

    useEffect(() => {
        if (scrollRef.current && titleRef.current) {
            const titleHeight = titleRef.current.clientHeight;
            const itemHeights = itemRefs.current
                .map((item) => item.clientHeight)
                .slice(0, 2);
            const rootFontSize = Number.parseFloat(
                getComputedStyle(document.documentElement).fontSize,
            );
            const totalHeightInRem =
                itemHeights.reduce((acc, height) => acc + height, titleHeight) /
                rootFontSize;

            scrollRef.current.style.height = `${totalHeightInRem}rem`;
        }
    }, [validAtValues]);

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            dispatch(setIsPostCreateCertificateSuccess(false));
        }
    }, [isPostCreateCertificateSuccess, dispatch]);

    const handleDateChange = (
        event: ChangeEvent<HTMLInputElement>,
        employeeId: number,
    ): void => {
        const newValidAt = event.target.value;
        dispatch(setValidAtValue({ employeeId, validAt: newValidAt }));

        if (
            selectedQualification.validity_type ===
                QualificationValidityTypeEnum.FIXED_DATE &&
            selectedQualification.fixed_valid_until &&
            newValidAt
        ) {
            dispatch(
                setExpiresAtValue({
                    employeeId,
                    expiresAt: format(
                        selectedQualification.fixed_valid_until,
                        "dd-MM-yyyy",
                    ),
                }),
            );
        } else if (selectedQualification.validity_period && newValidAt) {
            let expiresAtDate = new Date(newValidAt);

            if (selectedQualification.validity_period.years) {
                expiresAtDate = addYears(
                    expiresAtDate,
                    selectedQualification.validity_period.years,
                );
            }

            if (selectedQualification.validity_period.months) {
                expiresAtDate = addMonths(
                    expiresAtDate,
                    selectedQualification.validity_period.months,
                );
            }

            const formattedExpiresAtDate = format(expiresAtDate, "dd-MM-yyyy");
            dispatch(
                setExpiresAtValue({
                    employeeId,
                    expiresAt: formattedExpiresAtDate,
                }),
            );
        }

        if (!newValidAt) {
            dispatch(setExpiresAtValue({ employeeId, expiresAt: "" }));
        }
    };

    const postData = selectedEmployees.map((employee: EmployeeInterface) => ({
        qualification: selectedQualification,
        employee: employee,
        selectedEducator: formValues?.selectedEducators?.[employee.id] || "",
        validAt: validAtValues[employee.id] || "",
        expiresAt: expiresAtValues[employee.id] || "",
        file: file[employee.id] || null,
    }));

    return (
        <div className={"c-create-certificates-modal__content"}>
            <form
                className={"c-create-certificates-modal__content__form"}
                onSubmit={(event) => {
                    event.preventDefault();
                    handleMultipleCertificates({
                        postData,
                        dispatch,
                        postCreateCertificate,
                    });
                }}
            >
                {error && <p className={"c-error"}>{error}</p>}
                <div
                    ref={scrollRef}
                    className={
                        "c-create-certificates-modal__content__form__scroll"
                    }
                >
                    <h3
                        className={
                            "c-create-certificates-modal__content__form__title"
                        }
                        ref={titleRef}
                    >
                        Datum behaald & documenten
                    </h3>
                    {selectedEmployees.map(
                        (employee: EmployeeInterface, index: number) => (
                            <div
                                key={employee.id}
                                ref={(el) => {
                                    itemRefs.current[index] = el!;
                                }}
                                className={
                                    "c-create-certificates-modal__content__form__item"
                                }
                            >
                                <div
                                    className={
                                        "c-create-certificates-modal__content__form__item__name"
                                    }
                                >
                                    <Person />
                                    <p>{getUserFullName(employee.user)}</p>
                                </div>
                                <div
                                    className={
                                        "c-create-certificates-modal__content__form__item__form-group__combined"
                                    }
                                >
                                    <DateInputFieldComponent
                                        id={employee.id}
                                        className={
                                            "c-create-certificates-modal__content__form__item__form-group"
                                        }
                                        value={validAtValues[employee.id] || ""}
                                        onChange={handleDateChange}
                                        expiresAt={expiresAtValues[employee.id]}
                                    />
                                    <FileInputFieldComponent
                                        id={employee.id}
                                        dispatch={dispatch}
                                        className={
                                            "c-create-certificates-modal__content__form__item__form-group"
                                        }
                                        setFile={(newFile: File) => {
                                            setFile((prevState) => ({
                                                ...prevState,
                                                [employee.id]: newFile,
                                            }));
                                        }}
                                    />
                                </div>
                                <SelectFieldComponent
                                    id={employee.id}
                                    options={getEducatorOptions(
                                        educatorData?.data,
                                    )}
                                    onChange={(newValue) => {
                                        dispatch(
                                            setFormValues({
                                                ...formValues,
                                                selectedEducators: {
                                                    ...formValues?.selectedEducators,
                                                    [employee.id]:
                                                        newValue.value,
                                                },
                                            }),
                                        );
                                    }}
                                    value={getEmployeeEducatorValue({
                                        formValues,
                                        employeeId: employee.id.toString(),
                                        educators:
                                            selectedQualification?.educators ||
                                            [],
                                    })}
                                    errorMessage={"Opleider is verplicht"}
                                    placeHolder={"Selecteer opleider..."}
                                    title={"Selecteer opleider"}
                                />
                                <hr />
                            </div>
                        ),
                    )}
                </div>
                <SubmitButtonComponent
                    isPostCreateCertificateLoading={isLoading}
                    className={
                        "c-create-certificates-modal__content__form__button"
                    }
                />
            </form>
        </div>
    );
};
